/**
 * Operações do menu de uma parcela, na ordem em que aparecem no dropdown.
 *
 * A lista é a paridade com os **15 botões do grid legado**
 * (`IndividualPayment/Parcels`) mais três ações que só existem no painel novo:
 * `reintegrate`, `traceability` e `orderTracking`. As condições de exibição de
 * cada uma vivem em `parcelOperationAvailability.ts`.
 *
 * Nada além disso: as duas ações de NF que o painel novo tinha e o legado não —
 * "Reemitir NF" e "Enviar arquivo da NF" — foram removidas do menu. O upload
 * segue existindo na **régua da tela de edição** da parcela
 * (`ParcelEditToolbar`), que é escopo próprio e não passa por este enum.
 *
 * `sendEmail` e `pay` continuam declaradas mas não executam: o `SendBillet` do
 * legado nunca foi exposto em `/api/v1`, e `pay` depende de formulário próprio.
 *
 * `downloadPdf` fecha a lista mas **não** aparece no menu: é uma ação por
 * boleto, exposta como ícone na linha do boleto junto de `reintegrate`, como no
 * frame `OzPIl` de `design/payment.pen`.
 */
export const EParcelOperation = {
  Pay: 'pay',
  Edit: 'edit',
  GenerateBillet: 'generateBillet',
  SendCharge: 'sendCharge',
  SendEmail: 'sendEmail',
  ReversePayment: 'reversePayment',
  RefundPayment: 'refundPayment',
  Activate: 'activate',
  Cancel: 'cancel',
  Reintegrate: 'reintegrate',
  ResetIntegration: 'resetIntegration',
  TakeAdvantageParcel: 'takeAdvantageParcel',
  OpenInvoice: 'openInvoice',
  InvoiceFuturePayment: 'invoiceFuturePayment',
  InvoiceFuturePaymentVanzolini: 'invoiceFuturePaymentVanzolini',
  CancelInvoice: 'cancelInvoice',
  Traceability: 'traceability',
  OrderTracking: 'orderTracking',
  DownloadPdf: 'downloadPdf',
} as const;

export type ParcelOperation = (typeof EParcelOperation)[keyof typeof EParcelOperation];

/**
 * As operações na ordem do menu, para iterar.
 *
 * Derivada de {@link EParcelOperation} em vez de listada de novo: com um `enum`
 * nativo do TypeScript seriam duas estruturas a manter em sincronia, e nada
 * acusaria a que ficasse para trás.
 */
export const PARCEL_OPERATIONS = Object.values(EParcelOperation);

/**
 * Ações da régua do cabeçalho do contrato.
 *
 * Três têm destino real: `editPerson` navega para `/persons/$personId`,
 * `editPayment` para a edição do plano e `addParcel` para
 * `/individual-payment/$financialPersonId/parcels/create`. Três são resolvidas
 * por modal de confirmação (ver `CONFIRMABLE_CONTRACT_ACTIONS`), `downloadBooklet`
 * é declarada indisponível por falta de endpoint, e o resto segue o padrão de
 * "não implementado" já usado na listagem.
 */
export const EContractHeaderAction = {
  EditPerson: 'editPerson',
  EditPayment: 'editPayment',
  DownloadBooklet: 'downloadBooklet',
  AddParcel: 'addParcel',
  CancelOpenParcels: 'cancelOpenParcels',
  OpenCancelledParcels: 'openCancelledParcels',
  ResetExtendedExpiration: 'resetExtendedExpiration',
  Renegotiation: 'renegotiation',
  InactivateFinancial: 'inactivateFinancial',
} as const;

export type ContractHeaderAction =
  (typeof EContractHeaderAction)[keyof typeof EContractHeaderAction];

/** As ações da régua na ordem em que aparecem, para iterar. */
export const CONTRACT_HEADER_ACTIONS = Object.values(EContractHeaderAction);

/**
 * Operações da parcela que só disparam um endpoint e por isso são resolvidas por
 * um modal de confirmação, sem tela nem formulário.
 *
 * Duas têm payload além do `parcelId`, e mesmo assim cabem aqui porque o dado
 * extra é escolhido no próprio modal ou vem de onde a ação foi disparada:
 * `reintegrate` aceita o `serial` do boleto (opcional — sem ele o backend
 * reintegra a parcela) e `cancelInvoice` aceita o booleano `reversePayment`.
 *
 * `pay` e `edit` continuam fora: pedem formulário. As duas antecipações de NF
 * (`invoiceFuturePayment*`) também, por pedirem corpo e e-mail no
 * `ParcelFuturePaymentModal`, e `refundPayment`, que pede valor e motivo no
 * `ParcelRefundModal`.
 *
 * `takeAdvantageParcel` entra aqui apesar de o legado abrir um diálogo com
 * campos: o `serialPaymentId` do boleto excedente é **opcional** no endpoint,
 * então a confirmação simples deixa o backend escolher o boleto.
 */
export const CONFIRMABLE_PARCEL_OPERATIONS = [
  EParcelOperation.Cancel,
  EParcelOperation.Activate,
  EParcelOperation.ReversePayment,
  EParcelOperation.ResetIntegration,
  EParcelOperation.Reintegrate,
  EParcelOperation.SendCharge,
  EParcelOperation.TakeAdvantageParcel,
  EParcelOperation.CancelInvoice,
] as const satisfies readonly ParcelOperation[];

export type ConfirmableParcelOperation = (typeof CONFIRMABLE_PARCEL_OPERATIONS)[number];

/**
 * A operação escolhida no menu é uma das cobertas por confirmação?
 *
 * @param operation - Operação escolhida no menu da parcela.
 * @returns `true` quando a operação abre um modal de confirmação.
 */
export function isConfirmableParcelOperation(
  operation: ParcelOperation,
): operation is ConfirmableParcelOperation {
  return (CONFIRMABLE_PARCEL_OPERATIONS as readonly ParcelOperation[]).includes(operation);
}

/**
 * Ações de nível-contrato resolvidas por modal de confirmação.
 *
 * `resetExtendedExpiration` não entra: o endpoint recebe uma lista de parcelas,
 * mas `IndividualPaymentParcelViewModel` não expõe campo de prorrogação, então o
 * frontend não tem como derivar o conjunto.
 */
export const CONFIRMABLE_CONTRACT_ACTIONS = [
  EContractHeaderAction.CancelOpenParcels,
  EContractHeaderAction.OpenCancelledParcels,
  EContractHeaderAction.InactivateFinancial,
] as const satisfies readonly ContractHeaderAction[];

export type ConfirmableContractAction = (typeof CONFIRMABLE_CONTRACT_ACTIONS)[number];

/**
 * A ação escolhida na régua do cabeçalho é uma das cobertas por confirmação?
 *
 * @param action - Ação escolhida na régua ou no menu "Mais ações".
 * @returns `true` quando a ação abre um modal de confirmação.
 */
export function isConfirmableContractAction(
  action: ContractHeaderAction,
): action is ConfirmableContractAction {
  return (CONFIRMABLE_CONTRACT_ACTIONS as readonly ContractHeaderAction[]).includes(action);
}

/**
 * Ações de contrato que operam sobre um conjunto de parcelas e enviam os ids no
 * corpo `number[]` da request.
 */
export const BATCH_CONTRACT_ACTIONS = [
  EContractHeaderAction.CancelOpenParcels,
  EContractHeaderAction.OpenCancelledParcels,
] as const satisfies readonly ConfirmableContractAction[];

export type BatchContractAction = (typeof BATCH_CONTRACT_ACTIONS)[number];

/**
 * A ação opera sobre um conjunto de parcelas?
 *
 * @param action - Ação de contrato coberta por confirmação.
 * @returns `true` quando a ação envia uma lista de ids de parcela.
 */
export function isBatchContractAction(
  action: ConfirmableContractAction,
): action is BatchContractAction {
  return (BATCH_CONTRACT_ACTIONS as readonly ConfirmableContractAction[]).includes(action);
}

/**
 * Resumo derivado da lista de parcelas.
 *
 * O backend não devolve nenhum agregado junto das parcelas, então as linhas de
 * apoio dos indicadores e o rodapé da tabela saem daqui.
 */
export interface ParcelSummary {
  readonly total: number;
  readonly paid: number;
  readonly open: number;
  /** Soma de `total` de todas as parcelas. */
  readonly expectedValue: number;
  /** Soma de `totalPaid` das parcelas que têm valor pago. */
  readonly paidValue: number;
}
